import React, { useCallback, useEffect, useState } from 'react';
import { Segmented, Spin } from 'antd';
import { InstHotspotApi } from './InstHotspotApi';
import { InstHotspotSearchBar } from './InstHotspotSearchBar';
import type {
  InstHotspotRankMode,
  InstHotspotSearchItem,
  InstHotspotStockDetail,
  InstHotspotThemeBrief,
  InstHotspotTimelineItem,
} from './InstHotspotType';
import { InstHotspotStockCard } from './InstHotspotStockCard';
import { InstHotspotMetricLabel } from './InstHotspotMetricLabel';
import { useStockLinkDetail } from '../../components/fin-web/useStockLinkDetail';
import {
  getTimelineDateLabel,
  isAShareStockCode,
  renderMarkdownBold,
  sortTimelineByPostDateDesc,
} from './InstHotspotUtil';
import {
  instHotspotLayoutCssVars,
  instHotspotSearchCardStyle,
  instHotspotSearchCssVars,
  instHotspotSearchLayoutCssVars,
  instHotspotSearchStyle,
  instHotspotThemeCardCssVars,
  instHotspotThemeCardStyle,
  instHotspotTimelineCssVars,
  instHotspotTimelineStyle,
  instHotspotRightPanelStyle,
  instHotspotRightPanelCssVars,
  instHotspotLeftPanelStyle,
  instHotspotLeftPanelCssVars,
  instHotspotSearchResultStyle,
  instHotspotColumnSectionTitleStyle,
} from './Config';
import '../intel/IntelligenceFeedPage.css';
import './InstHotspotPage.css';

export default function InstHotspotPage({
  spaceId,
  spaceProductId,
}: {
  spaceId: string;
  spaceProductId: string;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<InstHotspotSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hotKeywords, setHotKeywords] = useState<string[]>([]);

  const [rankMode, setRankMode] = useState<InstHotspotRankMode>('recent');
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [themes, setThemes] = useState<InstHotspotThemeBrief[]>([]);
  const [activeThemeId, setActiveThemeId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<InstHotspotTimelineItem[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [activeNewsId, setActiveNewsId] = useState<string | null>(null);
  const [stockDetail, setStockDetail] = useState<InstHotspotStockDetail | null>(null);
  const [stocksLoading, setStocksLoading] = useState(false);

  const isSearchMode = searchKeyword != null;

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);

  const loadThemes = useCallback(async () => {
    setLoadingThemes(true);
    try {
      const res = await InstHotspotApi.listThemes({ spaceId, spaceProductId, rankMode });
      const rows = res.themes ?? [];
      setThemes(rows);
      setHotKeywords(res.hotKeywords ?? []);
      setActiveThemeId((prev) => {
        if (rows.length === 0) return null;
        if (prev && rows.some((t) => t.id === prev)) return prev;
        return rows[0].id;
      });
    } finally {
      setLoadingThemes(false);
    }
  }, [spaceId, spaceProductId, rankMode]);

  const loadTimeline = useCallback(
    async (themeId: string) => {
      setTimelineLoading(true);
      try {
        const res = await InstHotspotApi.getThemeDetail({ spaceId, spaceProductId, themeId });
        const items = sortTimelineByPostDateDesc(res.timeline ?? []);
        setTimeline(items);
        if (items.length > 0) {
          setActiveNewsId((prev) => prev ?? items[0].id);
        } else {
          setActiveNewsId(null);
          setStockDetail(null);
        }
      } finally {
        setTimelineLoading(false);
      }
    },
    [spaceId, spaceProductId]
  );

  const loadStocks = useCallback(
    async (quickNewsId: string) => {
      setStocksLoading(true);
      try {
        const res = await InstHotspotApi.getQuickNewsStocks({
          spaceId,
          spaceProductId,
          quickNewsId,
        });
        setStockDetail(res);
      } finally {
        setStocksLoading(false);
      }
    },
    [spaceId, spaceProductId]
  );

  const runSearch = useCallback(
    async (keywordOverride?: string) => {
      const keyword = (keywordOverride ?? searchInput).trim();
      if (!keyword) return;
      setSearchInput(keyword);
      setSearchLoading(true);
      try {
        const res = await InstHotspotApi.searchQuickNews({ spaceId, spaceProductId, keyword });
        const items = res.items ?? [];
        setSearchKeyword(keyword);
        setSearchResults(items);
        if (items.length > 0) {
          setActiveNewsId(items[0].id);
        } else {
          setActiveNewsId(null);
          setStockDetail(null);
        }
      } finally {
        setSearchLoading(false);
      }
    },
    [searchInput, spaceId, spaceProductId]
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchKeyword(null);
    setSearchResults([]);
    setActiveNewsId(null);
    setStockDetail(null);
  }, []);

  useEffect(() => {
    void loadThemes();
  }, [loadThemes]);

  useEffect(() => {
    if (isSearchMode || !activeThemeId) return;
    void loadTimeline(activeThemeId);
  }, [activeThemeId, isSearchMode, loadTimeline]);

  useEffect(() => {
    if (!activeNewsId) return;
    void loadStocks(activeNewsId);
  }, [activeNewsId, loadStocks]);

  const visibleStocks = (stockDetail?.stocks ?? []).filter((s) =>
    isAShareStockCode(s.stockCode)
  );

  const markdownHighlight = {
    color: instHotspotTimelineStyle.markdownHighlightColor,
    fontWeight: instHotspotTimelineStyle.markdownHighlightFontWeight,
  };

  const keywordHighlightBackground = instHotspotSearchResultStyle.keywordHighlightBackground;

  const columnSectionTitleStyle = {
    fontSize: instHotspotColumnSectionTitleStyle.fontSize,
    color: instHotspotColumnSectionTitleStyle.color,
    fontWeight: instHotspotColumnSectionTitleStyle.fontWeight,
    marginBottom: instHotspotColumnSectionTitleStyle.marginBottom,
  };

  const rankSectionTitleStyle = {
    ...columnSectionTitleStyle,
    marginBottom: 0,
  };

  const activeThemeName =
    themes.find((t) => t.id === activeThemeId)?.name ?? '';

  const renderSearchText = (text: string | null | undefined) =>
    renderMarkdownBold(text, markdownHighlight, searchKeyword, keywordHighlightBackground);

  const renderStockPanel = () => (
    <div
      className="lf-inst-hotspot-col lf-inst-hotspot-col--right"
      style={instHotspotRightPanelCssVars()}
    >
      <div className="lf-inst-hotspot-col-header">
        <div
          className="lf-inst-hotspot-right-title"
          style={columnSectionTitleStyle}
        >
          {instHotspotRightPanelStyle.sectionTitle}
        </div>
      </div>
      <div className="lf-inst-hotspot-col-body">
        {stocksLoading ? (
          <Spin />
        ) : visibleStocks.length === 0 ? (
          <div className="lf-inst-hotspot-empty">
            {activeNewsId ? '该快报无关联个股' : '选择快报查看关联个股'}
          </div>
        ) : (
          <div className="lf-intel-stocks">
            {visibleStocks.map((s) => (
              <InstHotspotStockCard
                key={s.stockCode}
                item={s}
                stockLinkDetail={stockLinkDetail}
                requestStockDetailForLink={requestStockDetailForLink}
                stockNotesTab={buildStockNotesTabProps(s.stockCode.trim(), s.stockName)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const searchBar = (
    <div className="lf-inst-hotspot-search-wrap">
      <InstHotspotSearchBar
        value={searchInput}
        onChange={setSearchInput}
        onSearch={() => void runSearch()}
        onClear={clearSearch}
      />
      {hotKeywords.length > 0 ? (
        <div className="lf-inst-hotspot-hot-keywords">
          {hotKeywords.map((kw) => (
            <button
              key={kw}
              type="button"
              className="lf-inst-hotspot-hot-keyword"
              onClick={() => void runSearch(kw)}
            >
              {kw}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={`lf-inst-hotspot-page${isSearchMode ? ' lf-inst-hotspot-page--search' : ''}`}
      style={{
        backgroundColor: instHotspotSearchStyle.pageBackgroundColor,
        ...instHotspotSearchCssVars(),
        ...instHotspotLayoutCssVars(),
        ...(isSearchMode ? instHotspotSearchLayoutCssVars() : {}),
      }}
    >
      {isSearchMode ? (
        <div
          className="lf-inst-hotspot-search-stage"
          style={instHotspotSearchLayoutCssVars()}
        >
          {searchBar}
          <div className="lf-inst-hotspot-search-root">
          <div className="lf-inst-hotspot-col lf-inst-hotspot-col--search-left">
            {searchLoading ? (
              <div className="lf-inst-hotspot-col-body">
                <Spin />
              </div>
            ) : searchResults.length === 0 ? (
              <div className="lf-inst-hotspot-col-body">
                <div className="lf-inst-hotspot-empty">未找到相关快报</div>
              </div>
            ) : (
              <>
                <div className="lf-inst-hotspot-col-header">
                  <div
                    className="lf-inst-hotspot-section-title"
                    style={columnSectionTitleStyle}
                  >
                    共{searchResults.length}条结果
                  </div>
                </div>
                <div className="lf-inst-hotspot-col-body">
                <div
                  className="lf-inst-hotspot-search-list"
                  style={instHotspotTimelineCssVars()}
                >
                  {searchResults.map((item) => {
                    const isActive = activeNewsId === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`lf-inst-hotspot-search-card${
                          isActive ? ' lf-inst-hotspot-search-card--active' : ''
                        }`}
                        style={{
                          borderRadius: instHotspotSearchCardStyle.borderRadius,
                          backgroundColor: isActive
                            ? instHotspotSearchCardStyle.activeBackgroundColor
                            : instHotspotSearchCardStyle.backgroundColor,
                          padding: instHotspotSearchCardStyle.padding,
                          marginBottom: instHotspotSearchCardStyle.marginBottom,
                          cursor: instHotspotSearchCardStyle.cursor,
                        }}
                        onClick={() => setActiveNewsId(item.id)}
                      >
                        <div className="lf-inst-hotspot-timeline-title">
                          {renderSearchText(item.title)}
                        </div>
                        <div className="lf-inst-hotspot-timeline-date">
                          {getTimelineDateLabel(item.postDate)}
                        </div>
                        <div className="lf-inst-hotspot-timeline-content">
                          {renderSearchText(item.content)}
                          <span className="lf-inst-hotspot-timeline-stock-count">
                            {' '}
                            · 关联个股 {item.referStockCount ?? 0}
                          </span>
                        </div>
                        <div
                          className="lf-inst-hotspot-search-card-footer"
                          style={{
                            borderTop: `1px solid ${instHotspotSearchCardStyle.footerBorderColor}`,
                            marginTop: instHotspotSearchCardStyle.footerMarginTop,
                            paddingTop: instHotspotSearchCardStyle.footerPaddingTop,
                          }}
                        >
                          <div
                            className="lf-inst-hotspot-search-card-theme-name"
                            style={{
                              color: instHotspotSearchCardStyle.themeNameColor,
                              fontSize: instHotspotSearchCardStyle.themeNameFontSize,
                            }}
                          >
                            {item.themeName || '—'}
                          </div>
                          <div
                            className="lf-inst-hotspot-search-card-metrics"
                            style={{
                              ['--lf-ih-search-metric-value-offset-y' as string]: `${instHotspotSearchCardStyle.footerMetricValueOffsetY}px`,
                            }}
                          >
                            <div className="lf-inst-hotspot-search-card-metric-inline">
                              <InstHotspotMetricLabel
                                label="热度"
                                tooltip={instHotspotThemeCardStyle.heatTooltip}
                                style={{
                                  color: instHotspotThemeCardStyle.metaLabelColor,
                                  fontSize: instHotspotThemeCardStyle.metaLabelFontSize,
                                }}
                              />
                              <span
                                className="lf-inst-hotspot-theme-metric-value"
                                style={{
                                  color: instHotspotThemeCardStyle.heatValueColor,
                                  fontSize: instHotspotThemeCardStyle.metricValueFontSize,
                                  fontFamily: instHotspotThemeCardStyle.metricValueFontFamily,
                                }}
                              >
                                {item.heat ?? '—'}
                              </span>
                            </div>
                            <div className="lf-inst-hotspot-search-card-metric-inline">
                              <InstHotspotMetricLabel
                                label="密度"
                                tooltip={instHotspotThemeCardStyle.densityTooltip}
                                style={{
                                  color: instHotspotThemeCardStyle.metaLabelColor,
                                  fontSize: instHotspotThemeCardStyle.metaLabelFontSize,
                                }}
                              />
                              <span
                                className="lf-inst-hotspot-theme-metric-value"
                                style={{
                                  color: instHotspotThemeCardStyle.densityValueColor,
                                  fontSize: instHotspotThemeCardStyle.metricValueFontSize,
                                  fontFamily: instHotspotThemeCardStyle.metricValueFontFamily,
                                }}
                              >
                                {item.density ?? '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </>
            )}
          </div>
          {renderStockPanel()}
          </div>
        </div>
      ) : (
        <>
          {searchBar}
          <div className="lf-inst-hotspot-root" style={instHotspotLayoutCssVars()}>
          <div
            className="lf-inst-hotspot-col lf-inst-hotspot-col--left"
            style={{ ...instHotspotLayoutCssVars(), ...instHotspotThemeCardCssVars() }}
          >
            <div
              className="lf-inst-hotspot-col-header lf-inst-hotspot-col-header--rank"
              style={instHotspotLeftPanelCssVars()}
            >
              <div
                className="lf-inst-hotspot-section-title"
                style={rankSectionTitleStyle}
              >
                {instHotspotLeftPanelStyle.sectionTitle}
              </div>
              <Segmented
                className="lf-inst-hotspot-rank-segmented"
                options={[
                  { label: '最近', value: 'recent' },
                  { label: '最新', value: 'latest' },
                ]}
                value={rankMode}
                onChange={(v) => setRankMode(v as InstHotspotRankMode)}
              />
            </div>
            <div className="lf-inst-hotspot-col-body">
            {loadingThemes ? (
              <Spin />
            ) : themes.length === 0 ? (
              <div className="lf-inst-hotspot-empty">暂无主题</div>
            ) : (
              <>
                {themes.map((t, index) => {
                const isActive = activeThemeId === t.id;
                return (
                  <div
                    key={t.id}
                    className="lf-inst-hotspot-theme-card"
                    style={{
                      borderRadius: instHotspotThemeCardStyle.borderRadius,
                      backgroundColor: isActive
                        ? instHotspotThemeCardStyle.activeBackgroundColor
                        : instHotspotThemeCardStyle.backgroundColor,
                      padding: instHotspotThemeCardStyle.padding,
                      marginBottom: instHotspotThemeCardStyle.marginBottom,
                      cursor: instHotspotThemeCardStyle.cursor,
                    }}
                    onClick={() => {
                      setActiveThemeId(t.id);
                      setActiveNewsId(null);
                    }}
                  >
                    <div className="lf-inst-hotspot-theme-card-body">
                      <div
                        className="lf-inst-hotspot-theme-rank-col"
                        style={{
                          color: instHotspotThemeCardStyle.rankColor,
                          fontSize: instHotspotThemeCardStyle.rankFontSize,
                          fontFamily: instHotspotThemeCardStyle.rankFontFamily,
                        }}
                      >
                        {index + 1}
                      </div>
                      <div className="lf-inst-hotspot-theme-main-col">
                        <div
                          className="lf-inst-hotspot-theme-name"
                          style={{
                            color: instHotspotThemeCardStyle.titleColor,
                            fontSize: instHotspotThemeCardStyle.titleFontSize,
                          }}
                        >
                          {t.name}
                        </div>
                        <div
                          className="lf-inst-hotspot-theme-metrics-col"
                          style={{ gap: instHotspotThemeCardStyle.metricBlockGap }}
                        >
                          <div className="lf-inst-hotspot-theme-metric-block">
                            <div
                              className="lf-inst-hotspot-theme-metric-value"
                              style={{
                                color: instHotspotThemeCardStyle.heatValueColor,
                                fontSize: instHotspotThemeCardStyle.metricValueFontSize,
                                fontFamily: instHotspotThemeCardStyle.metricValueFontFamily,
                              }}
                            >
                              {t.heat ?? '—'}
                            </div>
                            <InstHotspotMetricLabel
                              label="热度"
                              tooltip={instHotspotThemeCardStyle.heatTooltip}
                              style={{
                                color: instHotspotThemeCardStyle.metaLabelColor,
                                fontSize: instHotspotThemeCardStyle.metaLabelFontSize,
                              }}
                            />
                          </div>
                          <div className="lf-inst-hotspot-theme-metric-block">
                            <div
                              className="lf-inst-hotspot-theme-metric-value"
                              style={{
                                color: instHotspotThemeCardStyle.densityValueColor,
                                fontSize: instHotspotThemeCardStyle.metricValueFontSize,
                                fontFamily: instHotspotThemeCardStyle.metricValueFontFamily,
                              }}
                            >
                              {t.density ?? '—'}
                            </div>
                            <InstHotspotMetricLabel
                              label="密度"
                              tooltip={instHotspotThemeCardStyle.densityTooltip}
                              style={{
                                color: instHotspotThemeCardStyle.metaLabelColor,
                                fontSize: instHotspotThemeCardStyle.metaLabelFontSize,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </>
            )}
            </div>
          </div>
          <div className="lf-inst-hotspot-col lf-inst-hotspot-col--center">
            <div className="lf-inst-hotspot-col-header">
              <div
                className="lf-inst-hotspot-section-title"
                style={columnSectionTitleStyle}
              >
                {activeThemeName ? (
                  <>
                    <span
                      style={{
                        color: instHotspotTimelineStyle.sectionActiveThemeNameColor,
                      }}
                    >
                      {activeThemeName}
                    </span>
                    {' 快报列表'}
                  </>
                ) : (
                  '快报列表'
                )}
              </div>
            </div>
            <div className="lf-inst-hotspot-col-body">
            {timelineLoading ? (
              <Spin />
            ) : timeline.length === 0 ? (
              <div className="lf-inst-hotspot-empty">请选择主题查看快报</div>
            ) : (
              <div
                className="lf-inst-hotspot-timeline"
                style={{
                  gap: instHotspotTimelineStyle.itemGap,
                  ...instHotspotTimelineCssVars(),
                }}
              >
                {timeline.map((item) => {
                  const isActive = activeNewsId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`lf-inst-hotspot-timeline-item${
                        isActive ? ' lf-inst-hotspot-timeline-item--active' : ''
                      }`}
                      style={{
                        paddingBottom: instHotspotTimelineStyle.itemPaddingBottom,
                        backgroundColor: isActive
                          ? instHotspotTimelineStyle.activeBackgroundColor
                          : undefined,
                      }}
                      onClick={() => setActiveNewsId(item.id)}
                    >
                      <div className="lf-inst-hotspot-timeline-title">
                        {renderMarkdownBold(item.title, markdownHighlight)}
                      </div>
                      <div className="lf-inst-hotspot-timeline-date">
                        {getTimelineDateLabel(item.postDate)}
                      </div>
                      <div className="lf-inst-hotspot-timeline-content">
                        {renderMarkdownBold(item.content, markdownHighlight)}
                        <span className="lf-inst-hotspot-timeline-stock-count">
                          {' '}
                          · 关联个股 {item.referStockCount ?? 0}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
          {renderStockPanel()}
        </div>
        </>
      )}
    </div>
  );
}
